import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../App";
import { Search, Plus, ExternalLink, FileText, ChevronDown, ChevronRight } from "lucide-react";
import usePullToRefresh from "../hooks/usePullToRefresh";
import MobileSkeleton from "../components/MobileSkeleton";
import useShortcuts from "../hooks/useShortcuts";
import useUrlState from "../hooks/useUrlState";
import { EmptyState } from "../components/ui/empty-state";

/* ═══════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════ */
const ASSET_TYPES = ["reels", "statics", "stories", "carousels", "influencers"];

function daysSince(dateStr) {
  if (!dateStr) return null;
  try { return Math.round((Date.now() - new Date(dateStr).getTime()) / 86400000); } catch { return null; }
}

function relativeTime(dateStr) {
  const d = daysSince(dateStr);
  if (d === null) return "—";
  if (d === 0) return "Today";
  if (d === 1) return "Yesterday";
  if (d <= 7) return `${d}d ago`;
  if (d <= 30) return `${Math.floor(d / 7)}w ago`;
  return `${Math.floor(d / 30)}mo ago`;
}

function getInitials(name) {
  if (!name) return "?";
  const p = name.trim().split(/\s+/);
  return (p[0]?.[0] || "") + (p[1]?.[0] || "");
}

function CompanyLogo({ name, website, size = 36 }) {
  const [failed, setFailed] = useState(false);
  const domain = website && website !== "Unknown" ? website.replace(/^https?:\/\//, "").replace(/\/.*$/, "") : null;
  if (domain && !failed) {
    return <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=128`}
      width={size} height={size} alt={name}
      style={{ borderRadius: size > 30 ? 10 : 6, objectFit: "contain", background: "var(--s3)" }}
      onError={() => setFailed(true)} />;
  }
  return (
    <div style={{ width: size, height: size, borderRadius: size > 30 ? 10 : 6, display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.33, fontWeight: 700, background: "var(--s3)", color: "var(--t2)" }}>
      {getInitials(name)}
    </div>
  );
}

/* SOW as single % */
function getSOWPercent(account) {
  if (!account.is_retainer || !account.retainer_sow) return null;
  const sow = account.retainer_sow;
  const delivered = account.sow_delivered || {};
  const active = ASSET_TYPES.filter(t => (sow[t] || 0) > 0);
  if (active.length === 0) return null;
  let total = 0;
  active.forEach(t => { const tgt = sow[t] || 0; total += tgt > 0 ? (delivered[t] || 0) / tgt : 0; });
  return Math.round((total / active.length) * 100);
}

/* ═══════════════════════════════════════
   HEALTH — determines grouping + dot color
   ═══════════════════════════════════════ */
function getAccountHealth(account) {
  const activeBriefs = account.active_brief_count || 0;
  const days = daysSince(account.updated_at || account.won_date);
  const sowPct = getSOWPercent(account);

  let deadlineDays = null;
  if (account.next_deadline) {
    const now = new Date(); now.setHours(0,0,0,0);
    const d = new Date(account.next_deadline + "T00:00:00"); d.setHours(0,0,0,0);
    deadlineDays = Math.round((d - now) / 86400000);
  }

  // Red tier
  if (sowPct !== null && sowPct < 40) return { tier: "action", label: "SOW behind", color: "#EF4444" };
  if (deadlineDays !== null && deadlineDays < 0) return { tier: "action", label: "Overdue", color: "#EF4444" };
  if (days !== null && days > 30 && activeBriefs === 0) return { tier: "action", label: "Going cold", color: "#EF4444" };

  // Amber tier
  if (deadlineDays !== null && deadlineDays >= 0 && deadlineDays <= 7) return { tier: "week", label: `Due in ${deadlineDays}d`, color: "#F59E0B" };
  if (days !== null && days > 14 && activeBriefs === 0) return { tier: "week", label: "Needs attention", color: "#F59E0B" };
  if (activeBriefs === 0 && (account.brief_count || 0) > 0) return { tier: "week", label: "Idle", color: "#F59E0B" };
  if (sowPct !== null && sowPct < 70) return { tier: "week", label: `SOW ${sowPct}%`, color: "#F59E0B" };

  // Green
  return { tier: "healthy", label: "On track", color: "#22C55E" };
}

/* ═══════════════════════════════════════
   ACCOUNT CARD — detailed, visual
   ═══════════════════════════════════════ */
function AccountCard({ account, navigate }) {
  const health = getAccountHealth(account);
  const sowPct = getSOWPercent(account);
  const activeBriefs = account.active_brief_count || 0;
  const totalBriefs = account.brief_count || 0;

  return (
    <div onClick={() => navigate(`/bizdev/accounts/${account.lead_id}`)}
      style={{
        background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 14,
        padding: 20, cursor: "pointer", transition: "all .2s",
        borderLeft: `3px solid ${health.color}`,
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,.08)"; }}
      onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}
    >
      {/* Top: Logo + Name + Type */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <CompanyLogo name={account.company_name} website={account.website} size={40} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--t1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {account.company_name}
          </div>
          <div style={{ fontSize: 11, color: "var(--t3)", marginTop: 1 }}>
            {account.is_retainer ? "Retainer" : "Project"}{account.industry && account.industry !== "Unknown" ? ` · ${account.industry}` : ""}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: health.color }} />
          <span style={{ fontSize: 11, fontWeight: 600, color: health.color }}>{health.label}</span>
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display: "flex", gap: 16, marginBottom: 12 }}>
        <div style={{ flex: 1, background: "var(--s2)", borderRadius: 8, padding: "8px 12px" }}>
          <div style={{ fontSize: 10, color: "var(--t3)", fontFamily: "var(--font-mono)", letterSpacing: ".08em", marginBottom: 2 }}>BRIEFS</div>
          <span style={{ fontSize: 18, fontWeight: 700, fontFamily: "var(--font-mono)", color: "var(--t1)" }}>{activeBriefs}</span>
          <span style={{ fontSize: 11, color: "var(--t3)", fontFamily: "var(--font-mono)" }}> / {totalBriefs}</span>
        </div>
        {sowPct !== null && (
          <div style={{ flex: 1, background: "var(--s2)", borderRadius: 8, padding: "8px 12px" }}>
            <div style={{ fontSize: 10, color: "var(--t3)", fontFamily: "var(--font-mono)", letterSpacing: ".08em", marginBottom: 2 }}>SOW</div>
            <span style={{ fontSize: 18, fontWeight: 700, fontFamily: "var(--font-mono)", color: sowPct >= 70 ? "var(--g)" : sowPct >= 40 ? "var(--a)" : "var(--r)" }}>{sowPct}%</span>
          </div>
        )}
        <div style={{ flex: 1, background: "var(--s2)", borderRadius: 8, padding: "8px 12px" }}>
          <div style={{ fontSize: 10, color: "var(--t3)", fontFamily: "var(--font-mono)", letterSpacing: ".08em", marginBottom: 2 }}>ACTIVITY</div>
          <span style={{ fontSize: 13, fontWeight: 600, fontFamily: "var(--font-mono)", color: "var(--t2)" }}>{relativeTime(account.updated_at)}</span>
        </div>
      </div>

      {/* Bottom: AM + active brief titles */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 11, color: "var(--t3)" }}>
          {account.account_manager && <span>AM: {account.account_manager}</span>}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={(e) => { e.stopPropagation(); navigate(`/bizdev/briefs?new=1&lead_id=${account.lead_id}`); }}
            style={{ fontSize: 11, padding: "4px 10px", borderRadius: 6, background: "var(--rd)", color: "var(--r)", border: "none", cursor: "pointer", fontWeight: 600 }}>
            + Brief
          </button>
        </div>
      </div>

      {/* Active brief titles preview */}
      {(account.active_brief_titles || []).length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--bdr)" }}>
          {account.active_brief_titles.slice(0, 3).map((b, i) => (
            <span key={i} style={{ fontSize: 10, padding: "3px 8px", borderRadius: 6, background: "var(--s2)", color: "var(--t2)", fontFamily: "var(--font-mono)" }}>{b.title}</span>
          ))}
          {(account.active_brief_titles || []).length > 3 && <span style={{ fontSize: 10, color: "var(--t3)" }}>+{account.active_brief_titles.length - 3}</span>}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════
   SECTION HEADER — collapsible
   ═══════════════════════════════════════ */
function SectionHeader({ title, count, color, collapsed, onToggle }) {
  const Chevron = collapsed ? ChevronRight : ChevronDown;
  return (
    <button
      type="button"
      onClick={onToggle}
      style={{
        display: "flex", alignItems: "center", gap: 8, padding: "12px 20px 6px",
        width: "100%", background: "transparent", border: "none", cursor: "pointer",
      }}
    >
      <Chevron size={12} style={{ color: "var(--t3)", flexShrink: 0 }} />
      <div style={{ width: 8, height: 8, borderRadius: "50%", background: color }} />
      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".1em", fontFamily: "var(--font-mono)", color: "var(--t2)" }}>
        {title}
      </span>
      <span style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--t3)" }}>{count}</span>
    </button>
  );
}

/* ═══════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════ */
const COLLAPSE_STORAGE_KEY = "dm_accounts_collapsed";

export default function BizDevAccountsPage() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // URL-backed filters — reload preserves + shareable links
  const [search, setSearch] = useUrlState("q", "");
  const [typeFilter, setTypeFilter] = useUrlState("type", "all");
  const searchRef = useRef(null);

  // Section collapse state — persisted per user via localStorage.
  // Default: NEEDS ACTION open, THIS WEEK + ON TRACK collapsed.
  const [collapsed, setCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem(COLLAPSE_STORAGE_KEY) || '{"week":true,"healthy":true}'); }
    catch { return { week: true, healthy: true }; }
  });
  const toggleSection = useCallback((key) => {
    setCollapsed((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(next)); } catch (_) {}
      return next;
    });
  }, []);

  useShortcuts(
    [
      { keys: "/", description: "Focus search", group: "Clients",
        handler: () => searchRef.current?.focus() },
      { keys: "n", description: "New account",  group: "Clients",
        handler: () => navigate("/bizdev/leads/new") },
    ],
    { enabled: !loading }
  );

  const fetchAccounts = async () => {
    try { setLoading(true); const r = await api.get("/accounts"); setAccounts(r.data); }
    catch (err) { setError(err.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAccounts(); }, []);
  usePullToRefresh(fetchAccounts);

  const { actionItems, thisWeek, healthy } = useMemo(() => {
    let filtered = [...accounts];
    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter(a => a.company_name?.toLowerCase().includes(q) || a.account_manager?.toLowerCase().includes(q) || a.industry?.toLowerCase().includes(q));
    }
    if (typeFilter === "retainer") filtered = filtered.filter(a => a.is_retainer);
    if (typeFilter === "project") filtered = filtered.filter(a => !a.is_retainer);

    const action = [], week = [], ok = [];
    filtered.forEach(a => { const h = getAccountHealth(a); if (h.tier === "action") action.push(a); else if (h.tier === "week") week.push(a); else ok.push(a); });

    const sortFn = (a, b) => {
      if (a.next_deadline && b.next_deadline) return a.next_deadline.localeCompare(b.next_deadline);
      if (a.next_deadline) return -1; if (b.next_deadline) return 1;
      return (a.company_name || "").localeCompare(b.company_name || "");
    };
    action.sort(sortFn); week.sort(sortFn); ok.sort(sortFn);
    return { actionItems: action, thisWeek: week, healthy: ok };
  }, [accounts, search, typeFilter]);

  if (loading) return window.innerWidth < 768 ? <MobileSkeleton type="list" /> : <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 256 }}><span style={{ color: "var(--t3)", fontFamily: "var(--font-mono)" }}>Loading...</span></div>;
  if (error) return <div style={{ textAlign: "center", padding: 80 }}><p style={{ color: "#EF4444" }}>{error}</p></div>;

  const total = actionItems.length + thisWeek.length + healthy.length;

  return (
    <div className="dm-page-container" style={{ maxWidth: 1100, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: "var(--font-orb)", fontSize: 20, color: "var(--t1)", letterSpacing: ".05em" }}>CLIENTS</h1>
          <p style={{ fontSize: 12, color: "var(--t3)", marginTop: 2 }}>{accounts.length} active clients</p>
        </div>
        <button onClick={() => navigate("/bizdev/leads/new")}
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 20px", background: "var(--r)", color: "#fff", fontSize: 13, fontWeight: 700, borderRadius: 24, border: "none", cursor: "pointer", fontFamily: "var(--font-orb)" }}>
          <Plus size={16} /> New Account
        </button>
      </div>

      {/* Search + filter */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
        <div style={{ position: "relative", flex: 1, maxWidth: 320 }}>
          <Search size={15} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--t3)" }} />
          <input ref={searchRef} type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search..."
            style={{ width: "100%", paddingLeft: 36, paddingRight: 14, paddingTop: 9, paddingBottom: 9, background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 10, color: "var(--t1)", fontSize: 13 }} />
        </div>
        <div style={{ display: "flex", gap: 2 }}>
          {["all", "retainer", "project"].map(f => (
            <button key={f} onClick={() => setTypeFilter(f)}
              style={{ padding: "7px 14px", borderRadius: 8, fontSize: 12, fontWeight: 500, border: "none", background: typeFilter === f ? "var(--s3)" : "transparent", color: typeFilter === f ? "var(--t1)" : "var(--t3)", cursor: "pointer", textTransform: "capitalize" }}>
              {f === "all" ? "All" : f}
            </button>
          ))}
        </div>
        <span style={{ fontSize: 11, color: "var(--t3)", marginLeft: "auto", fontFamily: "var(--font-mono)" }}>{total}</span>
      </div>

      {/* Sections as card grids — collapsible, persisted per user */}
      {actionItems.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <SectionHeader title="NEEDS ACTION" count={actionItems.length} color="#EF4444"
            collapsed={!!collapsed.action} onToggle={() => toggleSection("action")} />
          {!collapsed.action && (
            <div className="dm-accounts-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))", gap: 12 }}>
              {actionItems.map(a => <AccountCard key={a.lead_id} account={a} navigate={navigate} />)}
            </div>
          )}
        </div>
      )}

      {thisWeek.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <SectionHeader title="THIS WEEK" count={thisWeek.length} color="#F59E0B"
            collapsed={!!collapsed.week} onToggle={() => toggleSection("week")} />
          {!collapsed.week && (
            <div className="dm-accounts-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))", gap: 12 }}>
              {thisWeek.map(a => <AccountCard key={a.lead_id} account={a} navigate={navigate} />)}
            </div>
          )}
        </div>
      )}

      {healthy.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <SectionHeader title="ON TRACK" count={healthy.length} color="#22C55E"
            collapsed={!!collapsed.healthy} onToggle={() => toggleSection("healthy")} />
          {!collapsed.healthy && (
            <div className="dm-accounts-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))", gap: 12 }}>
              {healthy.map(a => <AccountCard key={a.lead_id} account={a} navigate={navigate} />)}
            </div>
          )}
        </div>
      )}

      {total === 0 && (
        <EmptyState
          icon={FileText}
          title="No accounts match"
          message={search || typeFilter !== "all" ? "Try clearing filters or searching something else." : "Create your first account to get started."}
          cta={search || typeFilter !== "all"
            ? { label: "Clear filters", onClick: () => { setSearch(""); setTypeFilter("all"); } }
            : { label: "New account", onClick: () => navigate("/bizdev/leads/new") }}
        />
      )}
    </div>
  );
}
